import React, { RefCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { DropDownAsync } from "@/components/forms/inputs/dropDownAsync";
import { api } from "@/helper/api";
import { RootState } from "@/components/state/store";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import { requiredRule } from "@/components/tables/fields/fieldRules";
import { CATEGORY_FIELD_NAME } from "@/components/tables/fields/fieldNameRules";
import { buildEntityRequestParameters } from "@/components/tables/fields/entityRequestParameters";
import { useEntityRequestFailureHandler } from "@/components/tables/fields/useEntityRequestFailureHandler";
import {
  toAsyncEntityPreselection,
  toEntityOption,
} from "@/components/tables/fields/entityOption";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

const MIN_SEARCH_LENGTH = 3;

export interface CrudAsyncEntitySearchFieldProps {
  field: Pick<
    CrudModalField,
    | "id"
    | "header"
    | "required"
    | "errorMessage"
    | "entity"
    | "multiselect"
    | "autoFocus"
    | "disableColumn"
    | "filterByCategory"
    | "userEntityValueField"
    | "additionalUrlParameters"
    | "useGlobalStateToImpersonate"
    | "getOnlyActiveUsers"
    | "additionalRoutePartsforGet"
  >;
  disabled?: boolean;
  companySubjectId?: string;
  subjectIdForBuildings?: unknown;
  limit?: string;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (value: any) => void;
  handleError?: (error: any) => void;
}

const CrudAsyncEntitySearchField = ({
  field,
  disabled,
  companySubjectId,
  subjectIdForBuildings,
  limit,
  validationTargetRef,
  onChange,
  handleError,
}: CrudAsyncEntitySearchFieldProps) => {
  const name = field.id as string;
  const { t } = useTranslation();
  const { control } = useFormContext();
  const selectedCategory = useWatch({ control, name: CATEGORY_FIELD_NAME });
  const globalSubject = useSelector((state: RootState) => state.subjectSearch);
  const handleRequestFailure = useEntityRequestFailureHandler(handleError);

  const { parametersToRender, additionalRouteParts } =
    buildEntityRequestParameters(field, {
      subdomain: window.location.hostname.split(".")[0],
      globalSubjectValue: globalSubject?.value,
      companySubjectId,
      subjectIdForBuildings,
      limit,
    });
  const locked =
    !!disabled ||
    !!field.disableColumn ||
    (field.filterByCategory && !selectedCategory);

  const handleSelectionChange = (
    selectedOption: any,
    setFormValue: (value: any) => void
  ) => {
    if (field.multiselect) {
      const selectedIds = Array.isArray(selectedOption)
        ? selectedOption.map((option: any) => option.value)
        : [];
      setFormValue(selectedIds);
      onChange?.(selectedIds);
      return;
    }

    const selectedId =
      selectedOption && typeof selectedOption === "object"
        ? selectedOption.value
        : "";
    setFormValue(selectedId);
    onChange?.(selectedId);
  };

  const loadOptions = (inputValue: string, finishCallback: any) => {
    const searchText = inputValue.trim();
    if (searchText.length < MIN_SEARCH_LENGTH) {
      finishCallback([]);
      return;
    }

    api.genericApiRequest({
      method: "get",
      entity: field.entity,
      parametersToRender: {
        ...parametersToRender,
        search: searchText,
        additionalUrlParameters: {
          ...(parametersToRender.additionalUrlParameters || {}),
        },
      },
      additionalRouteParts,
      successHandler: (res: any) => {
        let results = res?.data?.results || [];
        if (field.filterByCategory && selectedCategory) {
          results = results.filter((item: any) =>
            item?.category_set?.some(
              (category: any) => category?.id === selectedCategory
            )
          );
        }
        finishCallback(
          results
            .map((item: any) => toEntityOption(item, field, t))
            .filter((option: any) => option.value && option.label)
        );
      },
      failHandler: (error: any) => {
        handleRequestFailure(error);
        finishCallback([]);
      },
    });
  };

  return (
    <CrudFieldController
      name={name}
      control={control}
      rules={requiredRule(field.required, field.errorMessage)}
      validationTargetRef={validationTargetRef}
      className={styles.selectWrapper}
      render={({
        field: { onChange: setFormValue, ref, value, ...rest },
        error,
      }) => (
        <DropDownAsync
          {...rest}
          ref={ref}
          wrapperClassName={styles.dropDownSubjectSearch}
          name={field.id}
          label={field.header}
          mandatory={field.required}
          error={error}
          options={[]}
          isMulti={field.multiselect}
          autofocus={field.autoFocus}
          isClearable={true}
          isSearchable={true}
          disabled={locked}
          preExistingValue={toAsyncEntityPreselection(value, field, t)}
          onChange={(selectedOption: any) =>
            handleSelectionChange(selectedOption, setFormValue)
          }
          dataCallback={loadOptions}
        />
      )}
    />
  );
};

export default CrudAsyncEntitySearchField;
